import React, { JSX, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import {
  ArrowPathIcon,
  ExclamationTriangleIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline'
import { useAuth } from 'context/user'
import { useEntitlements } from 'context/plan'
import { LoadingPage, LoadingSpinner } from 'components/Loading'
import { graphqlRequest, formatLocalDateTime } from 'lib/helpers'
import { eventPath } from 'lib/paths'
import { EventPage, EventStatus } from 'types/api'
import listEventsPageQuery from 'graphql/listEventsPage.graphql'
import { CursorPager } from 'components/admin/CursorPager'
import { AdminListToolbar, ChipRow, PageSizePicker, SearchBox } from 'components/admin/AdminListToolbar'
import {
  createPagerGate,
  createRequestRace,
  pageNumberOf,
  parseChoice,
  parsePageSize,
  popToken,
  pushToken,
  resetTokens,
  tokenForPage
} from 'lib/adminListState'

const STATUS_DOT: Record<EventStatus, string> = {
  ACTIVE: 'bg-emerald-500',
  INACTIVE: 'bg-amber-500',
  ENDED: 'bg-zinc-500'
}

const STATUS_CHIPS: Array<{ key: string, label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'ACTIVE', label: 'Live' },
  { key: 'INACTIVE', label: 'Not started' },
  { key: 'ENDED', label: 'Ended' }
]

const STATUS_KEYS = STATUS_CHIPS.map((c) => c.key)

type PageItem = EventPage['items'][number]

const StreamRow = ({ row }: { row: PageItem }): JSX.Element => (
  <li data-testid={`allstreams-row-${row.eventId}`}>
    <Link
      to={eventPath(row.eventId)}
      className='flex flex-wrap items-center gap-3 rounded-lg border border-white/5 bg-dm-bg-accent/30 px-3 py-2.5 hover:border-brand/40'
    >
      <span className={`h-2 w-2 shrink-0 rounded-full ${STATUS_DOT[row.status]}`} title={row.status} />
      <span className='min-w-0 flex-1 truncate text-sm font-medium text-dm-text-primary'>{row.name}</span>
      <span className='shrink-0 font-mono text-[11px] text-dm-text-accent'>{row.slug}</span>
      <span className='shrink-0 text-[11px] text-dm-text-accent'>
        Created {formatLocalDateTime(row.createdAt)}
      </span>
    </Link>
  </li>
)

export const AllStreamsPage = (): JSX.Element | null => {
  const { auth0Token, streamToolsUser } = useAuth()
  const navigate = useNavigate()
  const { superuser } = useEntitlements()
  useEffect(() => {
    if (streamToolsUser != null && !superuser) {
      void navigate('/', { replace: true })
    }
  }, [streamToolsUser, superuser, navigate])

  const [searchParams, setSearchParams] = useSearchParams()
  const statusParsed = parseChoice(searchParams.get('status'), STATUS_KEYS, 'all', 'Status')
  const sizeParsed = parsePageSize(searchParams.get('size'), 50)
  const status = statusParsed.value
  const pageSize = sizeParsed.value
  const urlNotice = statusParsed.notice ?? sizeParsed.notice

  const [page, setPage] = useState<EventPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tokens, setTokens] = useState<string[]>(resetTokens())
  const [activeSearch, setActiveSearch] = useState<string | null>(null)
  const raceRef = useRef(createRequestRace())
  const gateRef = useRef(createPagerGate())
  const listKey = `${status}|${pageSize}`
  const [tokensKey, setTokensKey] = useState(listKey)
  if (tokensKey !== listKey) {
    setTokensKey(listKey)
    setTokens(resetTokens())
  }
  useLayoutEffect(() => {
    gateRef.current.drop()
  }, [listKey])

  const load = useCallback(async (stack: string[], search: string | null): Promise<void> => {
    if (auth0Token == null) return
    const requestId = raceRef.current.begin()
    gateRef.current.hold()
    setLoading(true)
    setError(null)
    try {
      const variables = search != null
        ? { limit: pageSize, search }
        : {
            limit: pageSize,
            nextToken: tokenForPage(stack),
            status: status === 'all' ? null : status
          }
      const { page: fetched } = await graphqlRequest<{ page: EventPage }>(
        listEventsPageQuery, variables, auth0Token
      )
      if (!raceRef.current.isCurrent(requestId)) return
      setPage(fetched)
      gateRef.current.settle(search != null ? null : fetched.nextToken)
    } catch (err) {
      if (!raceRef.current.isCurrent(requestId)) return
      gateRef.current.fail()
      setError(err instanceof Error ? err.message : 'Failed to load streams')
    } finally {
      if (raceRef.current.isCurrent(requestId)) setLoading(false)
    }
  }, [auth0Token, pageSize, status])

  useEffect(() => {
    void load(tokens, activeSearch)
  }, [load, tokens, activeSearch])

  const setUrlParam = (key: string, value: string | null): void => {
    const next = new URLSearchParams(globalThis.location.search)
    if (value == null) next.delete(key)
    else next.set(key, value)
    setSearchParams(next)
  }

  const onChip = (key: string): void => {
    gateRef.current.drop()
    setActiveSearch(null)
    setTokens(resetTokens())
    setLoading(true)
    setUrlParam('status', key === 'all' ? null : key)
  }

  const onPageSize = (size: number): void => {
    gateRef.current.drop()
    setTokens(resetTokens())
    setLoading(true)
    setUrlParam('size', String(size))
  }

  const onSearch = (term: string | null): void => {
    gateRef.current.drop()
    setTokens(resetTokens())
    setLoading(true)
    setActiveSearch(term)
  }

  if (streamToolsUser == null) return <div>Unauthorized</div>
  if (!superuser) return null
  if (auth0Token == null) return <LoadingPage />

  const items = page?.items ?? []
  const hasNext = activeSearch == null && page?.nextToken != null
  const moreMayFollow = hasNext && items.length < pageSize && status !== 'all'

  return (
    <div className='flex flex-1' data-testid='all-streams-page'>
      <main className='flex flex-1 flex-col overflow-y-auto px-4 py-4'>
        <header className='mb-6 flex items-center gap-3'>
          <RectangleStackIcon className='size-6 text-brand' aria-hidden='true' />
          <h1 className='text-2xl font-semibold text-dm-text-primary'>All streams</h1>
          <button
            onClick={() => { void load(tokens, activeSearch) }}
            disabled={loading}
            title='Refresh this page of streams'
            className='ml-auto inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-1.5 text-xs font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            <ArrowPathIcon className={`size-3.5 ${loading ? 'animate-spin' : ''}`} aria-hidden='true' />
            Refresh
          </button>
        </header>

        <p className='mb-3 text-xs text-dm-text-secondary'>
          Every stream on the platform, newest first. Your own streams
          stay in the sidebar; this staff list is server-paged so it
          holds up at any fleet size.
        </p>

        {urlNotice != null && (
          <p className='mb-3 text-xs text-amber-300' data-testid='allstreams-url-notice'>{urlNotice}</p>
        )}

        <AdminListToolbar>
          <ChipRow
            label='Stream status filter'
            chips={STATUS_CHIPS.map((c) => ({ key: c.key, label: c.label, active: activeSearch == null && status === c.key }))}
            onChip={onChip}
            testid='allstreams-chip'
          />
          <div className='flex flex-wrap items-center gap-3'>
            <SearchBox
              placeholder='Exact slug or stream id'
              label='Search streams by exact slug or stream id'
              activeTerm={activeSearch}
              onSubmit={onSearch}
              testid='allstreams-search'
            />
            <PageSizePicker value={pageSize} onChange={onPageSize} testid='allstreams-size' />
          </div>
        </AdminListToolbar>

        {error != null && (
          <div className='mt-3 flex items-start gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-xs text-red-300'>
            <ExclamationTriangleIcon className='size-4 shrink-0' aria-hidden='true' />
            <span className='flex-1'>{error}</span>
            <button
              onClick={() => { void load(tokens, activeSearch) }}
              className='shrink-0 rounded-md border border-red-500/30 px-2 py-1 text-[11px] font-medium hover:bg-red-500/20'
            >
              Retry
            </button>
          </div>
        )}

        {error == null && (loading && page == null
          ? (
            <div className='flex items-center justify-center py-16'>
              <LoadingSpinner />
            </div>
            )
          : items.length === 0
            ? (
              <p className='mt-4 rounded-lg border border-dashed border-white/10 px-4 py-8 text-center text-xs text-dm-text-secondary' data-testid='allstreams-empty'>
                {activeSearch != null
                  ? 'No stream matches. Search is an exact slug or an exact stream id.'
                  : 'No streams match this view yet.'}
              </p>
              )
            : (
              <ul className='mt-4 space-y-2' data-testid='allstreams-list'>
                {items.map((row) => <StreamRow key={row.eventId} row={row} />)}
              </ul>
              ))}

        <CursorPager
          page={activeSearch != null ? 1 : pageNumberOf(tokens)}
          hasNext={hasNext}
          loading={loading}
          moreMayFollow={moreMayFollow}
          onPrev={() => {
            if (!gateRef.current.takePrev()) return
            setLoading(true)
            setTokens((prev) => popToken(prev))
          }}
          onNext={() => {
            const token = gateRef.current.takeNext()
            if (token == null) return
            setLoading(true)
            setTokens((prev) => pushToken(prev, token))
          }}
          testid='allstreams-pager'
        />
      </main>
    </div>
  )
}
